import { useState } from "react";
import { Accordion } from "@remo-co/ui-core/src/components/Accordion";
import { AccordionDetails } from "@remo-co/ui-core/src/components/AccordionDetails";
import { AccordionSummary } from "@remo-co/ui-core/src/components/AccordionSummary";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { KeyboardArrowUp } from "@remo-co/ui-core/src/icons/KeyboardArrowUp";
import classNames from "classnames";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { selectIsDirectoryVisibleToAssistants } from "modules/companySettings/redux/selectors";
import { useStyles } from "./EventDirectorySection.styles";
import useCompanyEventDirectory from "../hooks/useCompanyEventDirectory";
import BlankState from "./BlankState";
import EventDirectoryItem from "./EventDirectoryItem";

interface IEventDirectorySectionProps {
  isEventAssistant: boolean;
  hasEvent: boolean;
}

const EventDirectorySection = ({
  isEventAssistant,
  hasEvent,
}: IEventDirectorySectionProps) => {
  const styles = useStyles();
  const isDirectoryVisibleToAssistants = useSelector(
    selectIsDirectoryVisibleToAssistants,
  );
  const { t } = useI18n(["eventDirectory"]);
  const [isExpanded, setIsExpanded] = useState(true);
  const { companyEventDirectories } = useCompanyEventDirectory();

  const toggleSection = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * We do not render the EventDirectorySection if they're an Event Assistant
   * unless the setting has been explicitly turned on
   */
  if (!hasEvent || (isEventAssistant && !isDirectoryVisibleToAssistants)) {
    return null;
  }

  return (
    <Accordion
      expanded={isExpanded}
      onChange={toggleSection}
      classes={{
        root: styles.root,
        expanded: styles.expanded,
      }}
      data-testid="event-directory-section"
    >
      <AccordionSummary
        classes={{
          root: styles.summary,
          content: styles.summaryContent,
          expandIconWrapper: styles.summaryExpandIcon,
        }}
        expandIcon={
          <KeyboardArrowUp
            className={classNames(styles.expandIcon, {
              [styles.collapseIcon]: isExpanded,
            })}
          />
        }
      >
        <Typography variant="h4" className={styles.title}>
          {t("eventDirectory:section.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: styles.detailsRoot }}>
        {companyEventDirectories.length ? (
          <Container
            flex
            justifyContent="flex-start"
            className={styles.itemContainer}
          >
            {companyEventDirectories.map((directory) => (
              <EventDirectoryItem
                key={directory.id}
                eventDirectory={directory}
              />
            ))}
          </Container>
        ) : (
          <BlankState />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default EventDirectorySection;
